import { makeStyles, Typography, Box, Grid, Chip } from '@material-ui/core';
import StorageIcon from '@material-ui/icons/Storage';
import ExtensionIcon from '@material-ui/icons/Extension';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useEffect, useState } from 'react';
import {
  Content,
  Header,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { ApiEntity } from '@backstage/catalog-model';

/**
 * The portal's APIs page.
 *
 * It lists what the catalog holds. Until DX-196 it listed seven hardcoded chips
 * in two cards, every one marked "coming-soon" and none of them registered
 * anywhere — while seventeen real APIs sat one link away in the explorer. So the
 * list here is read from the catalog on purpose: an API appears because the team
 * that owns it registered it, not because somebody edited this file.
 *
 * Promised in specs/api-docs.md.
 */

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    color: 'var(--ui-text)',
    fontWeight: 500,
    fontSize: '1.4rem',
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionIcon: {
    color: 'var(--ui-primary)',
    fontSize: '1.4rem',
  },
  card: {
    background: 'var(--ui-bg-elevated)',
    border: '1px solid var(--ui-border)',
    borderRadius: 12,
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  cardTitle: {
    color: 'var(--ui-text)',
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  cardIcon: {
    color: 'var(--ui-primary)',
    fontSize: '1.2rem',
  },
  cardDesc: {
    color: 'var(--ui-text-muted)',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    marginBottom: theme.spacing(2),
    flexGrow: 1,
  },
  chip: {
    backgroundColor: 'var(--ui-bg-muted)',
    color: 'var(--ui-text-muted)',
    border: '1px solid var(--ui-border)',
    fontSize: '0.75rem',
    height: 22,
    margin: '0 4px 4px 0',
  },
  meta: {
    color: 'var(--docs-card-muted)',
    fontSize: '0.8rem',
    marginBottom: theme.spacing(1.5),
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    color: 'var(--ui-primary)',
    fontSize: '0.875rem',
    fontWeight: 600,
    marginTop: theme.spacing(1),
    '&:hover': { opacity: 0.8 },
  },
  empty: {
    background: 'var(--ui-bg-elevated)',
    border: '1px solid var(--ui-border)',
    borderRadius: 12,
    padding: theme.spacing(4),
    textAlign: 'center' as const,
  },
  emptyTitle: {
    color: 'var(--ui-text)',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  emptyText: {
    color: 'var(--ui-text-muted)',
    fontSize: '0.9rem',
  },
  explorerCard: {
    background: 'var(--ui-bg-elevated)',
    border: '1px solid var(--ui-border)',
    borderRadius: 12,
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(2),
  },
  explorerText: {
    color: 'var(--ui-text-muted)',
    fontSize: '0.9rem',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid var(--ui-border)',
    margin: theme.spacing(4, 0),
  },
}));

/** The owner ref an entity carries, as the name a reader recognises. */
const ownerName = (api: ApiEntity): string | undefined => {
  const owner = api.spec?.owner;
  if (typeof owner !== 'string') return undefined;
  // `group:default/platform` and a bare `group:platform` both occur across the
  // registered APIs, so the name is whatever follows the last separator.
  return owner.split('/').pop()?.split(':').pop();
};

const ApiCard = ({ api }: { api: ApiEntity }) => {
  const classes = useStyles();
  const namespace = api.metadata.namespace ?? 'default';
  const title = api.metadata.title ?? api.metadata.name;
  const owner = ownerName(api);
  const system = typeof api.spec?.system === 'string' ? api.spec.system : null;

  return (
    <Box className={classes.card}>
      <Typography className={classes.cardTitle}>
        <span className={classes.cardIcon}>
          <StorageIcon />
        </span>
        {title}
      </Typography>
      <Typography className={classes.cardDesc}>
        {api.metadata.description ?? 'No description was published.'}
      </Typography>
      <Box>
        {api.spec?.type && (
          <Chip label={api.spec.type} className={classes.chip} size="small" />
        )}
        {api.spec?.lifecycle && (
          <Chip
            label={api.spec.lifecycle}
            className={classes.chip}
            size="small"
          />
        )}
      </Box>
      <Typography className={classes.meta}>
        {owner ? `Owned by ${owner}` : 'No owner published'}
        {system ? ` · ${system}` : ''}
      </Typography>
      <Link
        to={`/catalog/${namespace}/api/${api.metadata.name}/definition`}
        className={classes.link}
      >
        {/* An arrow, not the open-in-new mark: the contract opens here, in the
            portal, and that mark promises leaving it (DX-349). */}
        Read the specification{' '}
        <ArrowForwardIcon style={{ fontSize: '0.9rem' }} />
      </Link>
    </Box>
  );
};

export const ApiDocsPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const [apis, setApis] = useState<ApiEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    let live = true;
    catalogApi
      .getEntities({ filter: { kind: 'api' } })
      .then(response => {
        if (live) setApis(response.items as ApiEntity[]);
      })
      .catch(caught => {
        if (live) setError(caught as Error);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    // Guarded rather than fire-and-forget: a reader who leaves before the
    // catalog answers would otherwise have this set state on a page that is gone.
    return () => {
      live = false;
    };
  }, [catalogApi]);

  const body = () => {
    if (loading) return <Progress />;
    if (error) return <ResponseErrorPanel error={error} />;

    // An empty catalog is a normal state and gets said out loud. An empty frame
    // reads to a person exactly like a page that failed to load, which is the
    // lesson this page taught for as long as its seven chips were unopenable.
    if (!apis.length) {
      return (
        <Box className={classes.empty}>
          <Typography className={classes.emptyTitle}>No APIs found</Typography>
          <Typography className={classes.emptyText}>
            No API is registered in the catalog yet. An API appears here once
            the team that owns it publishes it from its own repository.
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {apis.map(api => (
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            key={`${api.metadata.namespace ?? 'default'}/${api.metadata.name}`}
          >
            <ApiCard api={api} />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Page themeId="tool">
      <Header
        title="API Reference"
        subtitle="OpenAPI & AsyncAPI specs across all domains"
      />
      <Content>
        <Typography className={classes.sectionTitle}>
          <StorageIcon className={classes.sectionIcon} />
          Registered APIs
        </Typography>
        {body()}

        <hr className={classes.divider} />

        <Typography className={classes.sectionTitle}>
          <ExtensionIcon className={classes.sectionIcon} />
          API Explorer
        </Typography>
        <Box className={classes.explorerCard}>
          <Box>
            <Typography
              style={{
                color: 'var(--ui-text)',
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              All registered APIs
            </Typography>
            <Typography className={classes.explorerText}>
              Filter and search every OpenAPI &amp; AsyncAPI spec in the
              Software Catalog.
            </Typography>
          </Box>
          <Link to="/api-explorer" className={classes.link}>
            Open API Explorer{' '}
            <ArrowForwardIcon style={{ fontSize: '0.9rem' }} />
          </Link>
        </Box>
      </Content>
    </Page>
  );
};
